<template>
  <div class="literaturePage">
    <div class="navBar">
      <button class="backBtn" type="button" @click="handleBack">
        <img src="../../assets/images/titleArrow.png" alt="">
      </button>
      <div class="navTitle">{{ navTitle }}</div>
      <button
        v-if="viewMode === 'main' && activeModule && activeModule.filterable"
        class="navAction"
        type="button"
        @click="openFilter"
      >
        筛选
      </button>
      <div v-else class="navAction navActionPlaceholder" />
    </div>

    <template v-if="viewMode === 'home'">
      <section class="modulePanel">
        <div class="moduleGrid">
          <button
            v-for="item in moduleList"
            :key="item.key"
            type="button"
            class="moduleItem"
            @click="openModule(item)"
          >
            <span class="moduleIcon" :class="{ custom: !item.icon }">
              <img v-if="item.icon" :src="item.icon" alt="">
              <span v-else class="pathIcon">
                <i />
              </span>
            </span>
            <span class="moduleName">{{ item.title }}</span>
          </button>
        </div>
      </section>
    </template>

    <template v-else-if="viewMode === 'main'">
      <div v-if="showSearchBox" class="searchBox">
        <img :src="ICON_SEARCH" alt="">
        <input
          v-model.trim="searchText"
          :placeholder="searchPlaceholder"
          type="search"
          @keyup.enter="handleSearch"
        >
        <button v-if="searchText" class="cleanBtn" type="button" @click="clearSearch">
          <img :src="ICON_CLEAN" alt="">
        </button>
        <button class="searchBtn" type="button" @click="handleSearch">搜索</button>
      </div>

      <div v-if="breadcrumbs.length > 0" class="pathTabs">
        <button type="button" :class="{ active: breadcrumbs.length === 0 }" @click="goRoot">
          {{ rootTabTitle }}
        </button>
        <button
          v-for="(item, index) in breadcrumbs"
          :key="item.id || index"
          type="button"
          :class="{ active: index === breadcrumbs.length - 1 }"
          @click="goBreadcrumb(index)"
        >
          {{ item.title }}
        </button>
      </div>

      <div v-if="selectedFilterLabels.length > 0" class="filterTags">
        <span v-for="item in selectedFilterLabels" :key="item">{{ item }}</span>
        <button type="button" @click="clearFilter">清除</button>
      </div>

      <div class="content" :class="{ medicineRoot: showMedicineCards }">
        <div v-if="loading" class="loadingBox">
          <van-loading color="#2f7cf6" />
        </div>

        <template v-else-if="showMedicineCards">
          <div
            v-for="item in displayList"
            :key="item.key"
            class="medicineCard"
            @click="handleItemClick(item)"
          >
            <img :src="medicineCardImage(item)" alt="">
          </div>
          <van-empty v-if="displayList.length === 0" description="暂无数据" />
        </template>

        <template v-else>
          <div
            v-for="item in displayList"
            :key="item.key"
            class="listItem"
            :class="{ selected: isLastBreadcrumb(item) }"
            @click="handleItemClick(item)"
          >
            <div class="itemMain">
              <div class="itemTitle">{{ item.title }}</div>
              <div v-if="item.desc" class="itemDesc">{{ item.desc }}</div>
              <div v-if="item.meta" class="itemMeta">{{ item.meta }}</div>
            </div>
            <span v-if="item.badge" class="itemBadge">{{ item.badge }}</span>
            <span class="arrowIcon" />
          </div>
          <van-empty v-if="displayList.length === 0" description="暂无数据" />
        </template>
      </div>

      <van-popup v-model="filterVisible" position="bottom" round>
        <div class="filterPanel">
          <div class="filterHeader">
            <strong>全部筛选</strong>
            <button class="closeBtn" type="button" @click="filterVisible = false">×</button>
          </div>
          <div class="filterBody">
            <div class="filterLeft">
              <button
                v-for="item in filterTabs"
                :key="item.key"
                type="button"
                :class="{ active: filterTab === item.key }"
                @click="setFilterTab(item.key)"
              >
                {{ item.title }}
              </button>
            </div>
            <div class="filterRight">
              <button
                v-for="item in activeFilterOptions"
                :key="item.id"
                type="button"
                class="tag"
                :class="{ selected: isFilterSelected(item) }"
                @click="toggleActiveFilter(item)"
              >
                {{ item.label || item.name }}
              </button>
              <van-empty v-if="activeFilterOptions.length === 0" description="暂无数据" />
            </div>
          </div>
          <div class="filterFooter">
            <button class="resetBtn" type="button" @click="clearFilter">重置</button>
            <button class="doneBtn" type="button" @click="applyFilter">完成</button>
          </div>
        </div>
      </van-popup>
    </template>

    <template v-else-if="viewMode === 'detail'">
      <div class="detailPage">
        <div v-if="detailLoading" class="loadingBox">
          <van-loading color="#2f7cf6" />
        </div>
        <template v-else>
          <div class="detailHeader">
            <h2>{{ detailTitle }}</h2>
            <p v-if="detailSubTitle">{{ detailSubTitle }}</p>
            <p v-if="detailTime">{{ detailTime }}</p>
          </div>

          <video
            v-if="detailVideoUrl"
            class="detailVideo"
            controls
            playsinline
            :src="detailVideoUrl"
          />

          <div v-if="detailCategoryText" class="detailMeta">
            {{ detailCategoryText }}
          </div>

          <div v-if="detailHtml" class="htmlContent" v-html="detailHtml" />

          <div v-if="detailSections.length > 0" class="detailSections">
            <section v-for="(item, index) in detailSections" :key="index">
              <h3>{{ item.repoTitle || item.title || item.name }}</h3>
              <p>{{ item.content || item.result || item.description }}</p>
            </section>
          </div>

          <div v-if="scoreRows.length > 0" class="scoreTable">
            <div class="sectionTitle">评分表</div>
            <div class="scoreRow header">
              <span>序号</span>
              <span>项目</span>
              <span>子项</span>
              <span>要求</span>
              <span>分值</span>
              <span>得分</span>
            </div>
            <div v-for="(item, index) in scoreRows" :key="index" class="scoreRow">
              <span>{{ item.number || index + 1 }}</span>
              <span>{{ item.option || '-' }}</span>
              <span>{{ item.optionChild || '-' }}</span>
              <span>{{ item.require || '-' }}</span>
              <span>{{ item.scoreValue || '-' }}</span>
              <span>{{ item.score || '-' }}</span>
            </div>
          </div>

          <a
            v-if="detailFileUrl"
            class="fileLink"
            :href="detailFileUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ detailFileName || '查看附件' }}
          </a>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import wx from 'weixin-js-sdk'
import Bridge from '@/components/dsbridge.js'
import { judgeClient } from '@/utils/index.js'
import {
  informationListPost,
  informationDetail,
  syKbDetail,
  informationCategoryList,
  syKbPage,
  categoryGetList,
  categoryGetTree
} from '@/api/literature/index.js'

const PAGE_SIZE = 100

const ICON_YYZD = require('@/assets/images/literature/home_type_icon_yyzd.webp')
const ICON_LCZN = require('@/assets/images/literature/home_type_icon_lczn.webp')
const ICON_LCPF = require('@/assets/images/literature/home_type_icon_lcpf.webp')
const ICON_ZCYZD = require('@/assets/images/literature/home_type_icon_zcyzd.webp')
const ICON_XYZD = require('@/assets/images/literature/home_type_icon_xyzd.webp')
const ICON_ZYZD = require('@/assets/images/literature/home_type_icon_zyzd.webp')
const ICON_JYBK = require('@/assets/images/literature/home_type_icon_jybk.webp')
const ICON_JCBK = require('@/assets/images/literature/home_type_icon_jcbk.webp')
const ICON_ZZCX = require('@/assets/images/literature/home_type_icon_zzcx.webp')
const ICON_JBCX = require('@/assets/images/literature/home_type_icon_jbcx.webp')
const ICON_SBCZ = require('@/assets/images/literature/home_type_icon_sbcx.webp')
const ICON_HYBK = require('@/assets/images/literature/home_type_icon_hydcx.webp')
const ICON_SEARCH = require('@/assets/images/literature/home_search1.webp')
const ICON_CLEAN = require('@/assets/images/literature/inquiry_clean.webp')
const IMG_MEDICINE_WESTERN = require('@/assets/images/literature/home_medication_western.webp')
const IMG_MEDICINE_TRADITIONAL = require('@/assets/images/literature/home_medication_traditional.webp')

const LITERATURE_MODULES = [
  {
    key: 'businessMedicine',
    title: '商业用药',
    subtitle: '按 Android 用药指导流程检索商业用药资料',
    action: 'homeActionYyzd',
    type: 1,
    mode: 'category',
    rootPid: '0',
    rootApi: 'informationCategory',
    icon: ICON_YYZD
  },
  {
    key: 'clinicalGuide',
    title: '临床指南',
    subtitle: '临床指南资料查询',
    action: 'homeActionLczn',
    type: 9,
    mode: 'sykb',
    directPage: true,
    filterable: true,
    filterType: 9,
    icon: ICON_LCZN
  },
  {
    key: 'clinicalPath',
    title: '临床路径',
    subtitle: '临床路径资料查询',
    action: 'homeActionLclj',
    type: 3,
    mode: 'informationList'
  },
  {
    key: 'clinicalScore',
    title: '临床评分',
    subtitle: '临床评分表查询',
    action: 'homeActionLcpf',
    type: 4,
    mode: 'clinicalScore',
    icon: ICON_LCPF
  },
  {
    key: 'patentMedicine',
    title: '中成药指导',
    subtitle: '中成药用法与知识说明',
    action: 'homeActionZcyzd',
    type: 2,
    mode: 'category',
    rootPid: '1820297413269131266',
    icon: ICON_ZCYZD
  },
  {
    key: 'westernMedicine',
    title: '西药指导',
    subtitle: '西药用法与知识说明',
    action: 'homeActionXyzd',
    type: 2,
    mode: 'category',
    rootPid: '1818099582619348994',
    icon: ICON_XYZD
  },
  {
    key: 'chineseMedicine',
    title: '中药指导',
    subtitle: '中药用法与知识说明',
    action: 'homeActionZyzd',
    type: 2,
    mode: 'category',
    rootPid: '1818099654098677762',
    icon: ICON_ZYZD
  },
  {
    key: 'labTest',
    title: '检验百科',
    subtitle: '检验项目与结果说明',
    action: 'homeActionJybk',
    type: 3,
    mode: 'category',
    rootPid: '0',
    icon: ICON_JYBK
  },
  {
    key: 'examCheck',
    title: '检查百科',
    subtitle: '检查项目与结果说明',
    action: 'homeActionJcbk',
    type: 6,
    mode: 'category',
    rootPid: '0',
    icon: ICON_JCBK
  },
  {
    key: 'symptom',
    title: '症状查询',
    subtitle: '按症状检索医学知识',
    action: 'homeActionZzcx',
    type: 4,
    mode: 'sykb',
    directPage: true,
    filterable: true,
    filterType: 78,
    icon: ICON_ZZCX
  },
  {
    key: 'disease',
    title: '疾病查询',
    subtitle: '疾病百科与诊疗知识',
    action: 'homeActionJbcx',
    type: 5,
    mode: 'sykb',
    directPage: true,
    filterable: true,
    filterType: 78,
    icon: ICON_JBCX
  },
  {
    key: 'deviceOperation',
    title: '设备操作',
    subtitle: '医学设备操作资料',
    action: 'homeActionSbcx',
    type: 11,
    mode: 'category',
    rootPid: '0',
    icon: ICON_SBCZ
  },
  {
    key: 'chemicalLab',
    title: '化验百科',
    subtitle: '化验项目知识说明',
    action: 'homeActionHydcx',
    type: 12,
    mode: 'category',
    rootPid: '0',
    icon: ICON_HYBK
  }
]

export default {
  name: 'LiteratureKnowledge',
  data() {
    return {
      moduleList: LITERATURE_MODULES,
      activeKey: '',
      viewMode: 'home',
      loading: false,
      detailLoading: false,
      displayList: [],
      breadcrumbs: [],
      searchText: '',
      detailKind: '',
      detailData: {},
      detailSourceItem: null,
      filterVisible: false,
      isDirectEntry: false,
      filterCacheKey: '',
      bodyFilters: [],
      departmentFilters: [],
      selectedBodyIds: [],
      selectedDepartmentIds: [],
      activeDosageTypeId: '',
      filterTab: 'department',
      ICON_SEARCH,
      ICON_CLEAN
    }
  },
  computed: {
    activeModule() {
      return this.moduleList.find((item) => item.key === this.activeKey) || this.moduleList[0]
    },
    navTitle() {
      if (this.viewMode === 'home') {
        return '医学知识库'
      }
      if (this.viewMode === 'detail') {
        return this.detailKind === 'information' ? '详情' : '知识详情'
      }
      return this.activeModule.title
    },
    searchPlaceholder() {
      return '请输入关键词搜索'
    },
    rootTabTitle() {
      return '全部'
    },
    showSearchBox() {
      if (this.activeModule.key === 'businessMedicine') {
        return this.breadcrumbs.length === 0
      }
      return true
    },
    showMedicineCards() {
      return (
        this.activeModule.key === 'businessMedicine' &&
        this.breadcrumbs.length === 0 &&
        !this.searchText
      )
    },
    filterTabs() {
      const tabs = []
      if (this.departmentFilters.length > 0) {
        tabs.push({ key: 'department', title: '科室' })
      }
      if (this.activeModule.filterType !== 9 && this.bodyFilters.length > 0) {
        tabs.push({ key: 'body', title: '部位' })
      }
      return tabs
    },
    activeFilterOptions() {
      if (this.filterTab === 'body') {
        return this.bodyFilters
      }
      return this.departmentFilters
    },
    selectedFilterLabels() {
      const bodyLabels = this.bodyFilters
        .filter((item) => this.selectedBodyIds.includes(String(item.id)))
        .map((item) => item.label || item.name)
      const departmentLabels = this.departmentFilters
        .filter((item) => this.selectedDepartmentIds.includes(String(item.id)))
        .map((item) => item.label || item.name)
      return [...bodyLabels, ...departmentLabels].filter(Boolean)
    },
    detailTitle() {
      return this.detailData.title || this.detailData.name || this.detailData.label || '知识详情'
    },
    detailSubTitle() {
      if (this.detailKind === 'information') {
        return this.detailData.course ? `来源：${this.detailData.course}` : ''
      }
      return this.detailData.introduction || this.detailData.description || ''
    },
    detailTime() {
      if (!this.detailData.releaseTime && !this.detailData.updateTime) {
        return ''
      }
      return `发布时间：${this.detailData.releaseTime || this.detailData.updateTime}`
    },
    detailHtml() {
      return this.formatHtml(this.detailData.content)
    },
    detailSections() {
      return Array.isArray(this.detailData.repoDetails) ? this.detailData.repoDetails : []
    },
    scoreRows() {
      return Array.isArray(this.detailData.evaluateScoreList)
        ? this.detailData.evaluateScoreList
        : []
    },
    detailFileName() {
      return this.detailData.fileName || ''
    },
    detailFileUrl() {
      return this.formatFileUrl(this.detailData.fileUrl)
    },
    detailVideoUrl() {
      return this.formatFileUrl(this.detailData.videoUrl)
    },
    detailCategoryText() {
      const categories = Array.isArray(this.detailData.repoCategories)
        ? this.detailData.repoCategories
        : []
      const body = categories
        .filter((item) => Number(item.repoType) === 7 && item.label)
        .map((item) => item.label)
      const department = categories
        .filter((item) => Number(item.repoType) === 8 && item.label)
        .map((item) => item.label)
      const texts = []
      if (this.detailData.rxName) {
        texts.push(`处方：${this.detailData.rxName}`)
      }
      if (this.detailData.insured) {
        texts.push(`医保：${this.detailData.insured}`)
      }
      if (body.length > 0) {
        texts.push(`发病部位：${body.join('、')}`)
      }
      if (department.length > 0) {
        texts.push(`就诊科室：${department.join('、')}`)
      }
      return texts.join(' | ')
    }
  },
  created() {
    const shouldLoad = this.initFromRoute()
    if (shouldLoad) {
      this.loadActiveModule()
    }
  },
  methods: {
    initFromRoute() {
      const routeKey = this.$route.query.module || this.$route.query.tab || this.$route.query.key || this.$route.query.menu
      const routeAction = this.$route.query.action || this.$route.query.menuCode
      const routeType = this.$route.query.type
      const routePid = this.$route.query.pid
      const byKey = this.moduleList.find((item) => item.key === routeKey || item.action === routeKey)
      const byAction = this.moduleList.find((item) => item.action === routeAction)
      const byType = this.moduleList.find((item) => String(item.type) === String(routeType))

      if (byKey || byAction) {
        const target = byKey || byAction
        this.isDirectEntry = true
        this.activeKey = target.key
        this.viewMode = 'main'
        return true
      }
      if (routePid) {
        const customModule = {
          key: 'custom',
          title: this.$route.query.title || '知识库',
          subtitle: '医学知识资料查询',
          type: Number(routeType || 2),
          mode: 'category',
          rootPid: String(routePid)
        }
        this.moduleList = [customModule, ...this.moduleList]
        this.isDirectEntry = true
        this.activeKey = 'custom'
        this.viewMode = 'main'
        return true
      }
      if (routeType && byType) {
        this.isDirectEntry = true
        this.activeKey = byType.key
        this.viewMode = 'main'
        return true
      }
      return false
    },
    async openModule(item) {
      this.activeKey = item.key
      this.resetModuleState()
      await this.loadActiveModule()
    },
    async loadActiveModule() {
      this.viewMode = 'main'
      this.displayList = []
      this.breadcrumbs = []
      this.searchText = ''
      this.activeDosageTypeId = ''

      if (this.activeModule.mode === 'informationList') {
        await this.loadInformationList()
        return
      }

      if (this.activeModule.mode === 'clinicalScore') {
        await this.loadInformationCategoryList('0')
        return
      }

      if (this.activeModule.directPage) {
        await this.loadSykbPage()
        await this.ensureFilterData()
        return
      }

      await this.loadCategoryList(this.activeModule.rootPid || '0', { root: true })
    },
    resetModuleState() {
      this.displayList = []
      this.breadcrumbs = []
      this.searchText = ''
      this.detailData = {}
      this.detailSourceItem = null
      this.activeDosageTypeId = ''
      this.filterVisible = false
      this.filterTab = 'department'
      this.clearFilterState()
    },
    async clearSearch() {
      this.searchText = ''
      await this.loadActiveModule()
    },
    async handleSearch() {
      this.breadcrumbs = []
      this.activeDosageTypeId = ''
      if (!this.searchText) {
        await this.loadActiveModule()
        return
      }
      if (this.activeModule.mode === 'informationList') {
        await this.loadInformationList()
        return
      }
      if (this.activeModule.mode === 'clinicalScore') {
        await this.loadInformationList({ type: 4 })
        return
      }
      await this.loadSykbPage({ search: true })
    },
    async loadInformationList(options = {}) {
      this.loading = true
      try {
        const params = {
          type: options.type || this.activeModule.type,
          title: this.searchText,
          keyword: this.searchText,
          pageNum: 1,
          pageSize: PAGE_SIZE
        }
        if (options.categoryId) {
          params.categoryId = options.categoryId
        }
        const rows = await this.requestRows(() => informationListPost(params))
        this.displayList = rows.map((item, index) =>
          this.normalizeItem(item, index, 'information')
        )
      } finally {
        this.loading = false
      }
    },
    async loadInformationCategoryList(pid) {
      this.loading = true
      try {
        const params = {
          pid: pid || '0',
          type: this.activeModule.type
        }
        const rows = await this.requestRows(() => informationCategoryList(params))
        this.displayList = rows.map((item, index) =>
          this.normalizeItem(item, index, 'informationCategory')
        )
      } finally {
        this.loading = false
      }
    },
    async loadCategoryList(pid, options = {}) {
      this.loading = true
      try {
        const params = {
          pid: pid || '0',
          type: this.activeModule.type
        }
        if (this.searchText) {
          params.label = this.searchText
        }

        let rows = []
        let kind = 'category'
        if (options.root && this.activeModule.rootApi === 'informationCategory') {
          rows = await this.requestRows(() => informationCategoryList(params))
          if (rows.length === 0) {
            rows = await this.requestRows(() => categoryGetList(params))
          } else {
            kind = 'informationCategory'
          }
        } else {
          rows = await this.requestRows(() => categoryGetList(params))
        }

        this.displayList = rows.map((item, index) => this.normalizeItem(item, index, kind))
      } finally {
        this.loading = false
      }
    },
    async loadSykbPage(options = {}) {
      this.loading = true
      try {
        const params = {
          type: this.activeModule.type,
          pageNum: 1,
          pageSize: PAGE_SIZE
        }

        if (!options.search && options.categoryIds) {
          params.categoryIds = options.categoryIds
        }
        if (this.activeModule.type === 2 && options.dosageTypeId) {
          params.dosageTypeId = options.dosageTypeId
        }
        if (this.searchText) {
          params.keyword = this.searchText
        }
        if (this.selectedBodyIds.length > 0) {
          params.bodyIds = this.selectedBodyIds.join(',')
        }
        if (this.selectedDepartmentIds.length > 0) {
          params.departmentIds = this.selectedDepartmentIds.join(',')
        }

        const rows = await this.requestRows(() => syKbPage(params))
        this.displayList = rows.map((item, index) => {
          const normalized = this.normalizeItem(item, index, 'sykb')
          normalized.isData = true
          return normalized
        })
      } finally {
        this.loading = false
      }
    },
    async requestRows(requestFn) {
      const res = await requestFn()
      return this.getRows(this.getResponseData(res))
    },
    getResponseData(res) {
      if (res && Object.prototype.hasOwnProperty.call(res, 'data')) {
        return res.data
      }
      return res
    },
    getRows(data) {
      if (Array.isArray(data)) {
        return data.filter(Boolean)
      }
      if (!data) {
        return []
      }
      if (Array.isArray(data.rows)) {
        return data.rows.filter(Boolean)
      }
      if (Array.isArray(data.records)) {
        return data.records.filter(Boolean)
      }
      if (Array.isArray(data.list)) {
        return data.list.filter(Boolean)
      }
      if (data.data) {
        return this.getRows(data.data)
      }
      return []
    },
    normalizeItem(item, index, kind) {
      const id = item.id || item.categoryId || item.medicalKnowledgeId || index
      const title =
        item.label ||
        item.title ||
        item.name ||
        item.categoryName ||
        item.fileName ||
        '未命名'
      const desc = item.description || item.introduction || item.course || ''
      const meta = item.releaseTime || item.updateTime || item.publisher || ''
      const childNode = item.childNode === undefined ? -1 : Number(item.childNode)
      const isCategory = item.isCategory === undefined ? -1 : Number(item.isCategory)
      const isData =
        kind === 'information' ||
        kind === 'sykb' ||
        childNode === 99 ||
        isCategory === 0
      return {
        key: `${kind}-${id}-${index}`,
        id: String(id),
        title,
        desc,
        meta,
        badge: isData ? '' : childNode === 1 ? '内容' : '分类',
        childNode,
        isCategory,
        isData,
        dosageTypeId: item.pid ? String(item.pid) : '',
        kind,
        raw: item
      }
    },
    medicineCardImage(item) {
      const picUrl = item && item.raw ? item.raw.picUrl : ''
      if (picUrl) {
        return this.formatFileUrl(picUrl)
      }
      return /中药|中医/.test(item && item.title ? item.title : '')
        ? IMG_MEDICINE_TRADITIONAL
        : IMG_MEDICINE_WESTERN
    },
    isLastBreadcrumb(item) {
      if (this.breadcrumbs.length === 0) {
        return false
      }
      const last = this.breadcrumbs[this.breadcrumbs.length - 1]
      return String(last.id) === String(item.id)
    },
    async handleItemClick(item) {
      if (item.kind === 'information') {
        await this.openInformationDetail(item.id, item)
        return
      }

      if (this.activeModule.mode === 'clinicalScore') {
        await this.handleClinicalScoreItem(item)
        return
      }

      if (item.isData) {
        await this.openSykbDetail(item.id, item)
        return
      }

      const crumb = {
        id: item.id,
        title: item.title,
        childNode: item.childNode,
        dosageTypeId: item.dosageTypeId
      }
      this.breadcrumbs.push(crumb)

      if (item.childNode === 1) {
        this.activeDosageTypeId = item.dosageTypeId
        await this.loadSykbPage({
          categoryIds: item.id,
          dosageTypeId: item.dosageTypeId
        })
      } else {
        await this.loadCategoryList(item.id)
      }
    },
    async handleClinicalScoreItem(item) {
      if (item.isData || item.childNode === 99) {
        await this.openInformationDetail(item.id, item)
        return
      }

      const crumb = {
        id: item.id,
        title: item.title,
        childNode: item.childNode
      }
      this.breadcrumbs.push(crumb)

      if (this.breadcrumbs.length === 1) {
        await this.loadInformationCategoryList(item.id)
        return
      }
      await this.loadInformationList({ type: 4, categoryId: item.id })
    },
    async goRoot() {
      this.breadcrumbs = []
      this.activeDosageTypeId = ''
      await this.loadActiveModule()
    },
    async goBreadcrumb(index) {
      if (index < 0 || index >= this.breadcrumbs.length) {
        return
      }
      const target = this.breadcrumbs[index]
      this.breadcrumbs = this.breadcrumbs.slice(0, index + 1)

      if (this.activeModule.mode === 'clinicalScore') {
        if (index === 0) {
          await this.loadInformationCategoryList(target.id)
        } else {
          await this.loadInformationList({ type: 4, categoryId: target.id })
        }
        return
      }

      if (target.childNode === 1) {
        await this.loadSykbPage({
          categoryIds: target.id,
          dosageTypeId: target.dosageTypeId
        })
      } else {
        await this.loadCategoryList(target.id)
      }
    },
    async openInformationDetail(id, item) {
      if (!id) {
        return
      }
      this.detailKind = 'information'
      this.detailSourceItem = item
      this.detailData = {}
      this.viewMode = 'detail'
      this.detailLoading = true
      try {
        const res = await informationDetail(id)
        this.detailData = this.getResponseData(res) || {}
      } finally {
        this.detailLoading = false
      }
    },
    async openSykbDetail(id, item) {
      if (!id) {
        return
      }
      this.detailKind = 'sykb'
      this.detailSourceItem = item
      this.detailData = {}
      this.viewMode = 'detail'
      this.detailLoading = true
      try {
        const res = await syKbDetail(id)
        this.detailData = this.getResponseData(res) || {}
      } finally {
        this.detailLoading = false
      }
    },
    async ensureFilterData() {
      if (!this.activeModule.filterable) {
        return
      }
      if (this.filterCacheKey === this.activeModule.key) {
        return
      }
      this.bodyFilters = []
      this.departmentFilters = []
      try {
        if (this.activeModule.filterType === 9) {
          const departmentRes = await categoryGetTree({ type: 9 })
          this.departmentFilters = this.getRows(this.getResponseData(departmentRes))
          this.filterTab = this.departmentFilters.length > 0 ? 'department' : 'body'
          this.filterCacheKey = this.activeModule.key
          return
        }
        const [bodyRes, departmentRes] = await Promise.all([
          categoryGetTree({ type: 7 }),
          categoryGetTree({ type: 8 })
        ])
        this.bodyFilters = this.getRows(this.getResponseData(bodyRes))
        this.departmentFilters = this.getRows(this.getResponseData(departmentRes))
        this.filterTab = this.departmentFilters.length > 0 ? 'department' : 'body'
        this.filterCacheKey = this.activeModule.key
      } catch (error) {
        this.filterCacheKey = ''
        this.bodyFilters = []
        this.departmentFilters = []
      }
    },
    async openFilter() {
      await this.ensureFilterData()
      this.filterVisible = true
    },
    toggleBodyFilter(item) {
      this.selectedBodyIds = this.toggleId(this.selectedBodyIds, item.id)
    },
    toggleDepartmentFilter(item) {
      this.selectedDepartmentIds = this.toggleId(this.selectedDepartmentIds, item.id)
    },
    toggleId(list, id) {
      const value = String(id)
      if (list.includes(value)) {
        return list.filter((item) => item !== value)
      }
      return [...list, value]
    },
    isBodySelected(item) {
      return this.selectedBodyIds.includes(String(item.id))
    },
    isDepartmentSelected(item) {
      return this.selectedDepartmentIds.includes(String(item.id))
    },
    async applyFilter() {
      this.filterVisible = false
      await this.loadSykbPage()
    },
    async clearFilter() {
      this.clearFilterState()
      this.filterVisible = false
      if (this.activeModule.filterable) {
        await this.loadSykbPage()
      }
    },
    clearFilterState() {
      this.selectedBodyIds = []
      this.selectedDepartmentIds = []
    },
    setFilterTab(key) {
      this.filterTab = key
    },
    isFilterSelected(item) {
      if (this.filterTab === 'body') {
        return this.isBodySelected(item)
      }
      return this.isDepartmentSelected(item)
    },
    toggleActiveFilter(item) {
      if (this.filterTab === 'body') {
        this.toggleBodyFilter(item)
        return
      }
      this.toggleDepartmentFilter(item)
    },
    goHome() {
      this.viewMode = 'home'
      this.displayList = []
      this.breadcrumbs = []
      this.searchText = ''
      this.filterVisible = false
    },
    handleBack() {
      if (this.viewMode === 'detail') {
        this.viewMode = 'main'
        return
      }
      if (this.viewMode === 'main') {
        if (this.breadcrumbs.length > 0) {
          this.goRoot()
          return
        }
        if (this.isDirectEntry) {
          this.backToMiniHome()
          return
        }
        this.goHome()
        return
      }
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.closeHost()
    },
    backToMiniHome() {
      if (this.isWeChat()) {
        wx.miniProgram.switchTab({ url: '/pages/home/home' })
        return
      }
      this.closeHost()
    },
    closeHost() {
      if (this.isWeChat()) {
        wx.miniProgram.navigateBack()
        return
      }
      if (judgeClient() === 'Android') {
        Bridge.callmethod('WebAction.closeWeb', {}, () => {})
        return
      }
      try {
        window.webkit.messageHandlers.closeWeb.postMessage('close')
      } catch (error) {
        window.history.back()
      }
    },
    isWeChat() {
      return /micromessenger/i.test(navigator.userAgent)
    },
    formatFileUrl(url) {
      if (!url) {
        return ''
      }
      if (/^(https?:)?\/\//i.test(url)) {
        return encodeURI(url)
      }
      const prefix = String(this.imgUrl || 'https://file.medmeta.com/').replace(/\/+$/, '')
      const path = String(url).startsWith('/') ? String(url) : `/${url}`
      if (/\/file$/i.test(prefix) && path.startsWith('/file/')) {
        return encodeURI(`${prefix}${path.replace(/^\/file/, '')}`)
      }
      return encodeURI(`${prefix}${path}`)
    },
    formatHtml(html) {
      if (!html) {
        return ''
      }
      const filePrefix = String(this.imgUrl || 'https://file.medmeta.com/').replace(/\/+$/, '')
      return String(html)
        .replace(/<img/gi, '<img style="max-width:100%;height:auto;display:block;margin:10px 0;"')
        .replace(/src="\/?file\//gi, `src="${filePrefix}/`)
    }
  }
}
</script>

<style lang="scss" scoped>
.literaturePage {
  --lit-nav-height: 72px;
  --lit-page-gutter: 16px;
  --lit-soft-green: #1bbf8d;

  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(196, 249, 247, 0.5) 0, rgba(255, 255, 255, 0) 240px),
    #f5f7fb;
  color: #1f2937;
  padding-bottom: 18px;
  font-size: 14px;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }

  button,
  input,
  textarea {
    font-family: inherit;
  }

  .navBar {
    position: sticky;
    top: 0;
    z-index: 10;
    height: var(--lit-nav-height);
    padding: 44px var(--lit-page-gutter) 0;
    background: linear-gradient(
      180deg,
      rgba(196, 249, 247, 0.5),
      rgba(255, 255, 255, 0) 100%
    );
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: none;

    .backBtn,
    .navAction {
      width: 28px;
      height: 28px;
      padding: 0;
      border: 0;
      background: transparent;
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--lit-soft-green);
      font-size: 12px;
      line-height: 28px;
    }

    .backBtn {
      width: 20px;
      height: 20px;
      line-height: 20px;

      img {
        width: 20px;
        height: 20px;
      }
    }

    .navAction {
      border-radius: 50%;
      background: rgba(27, 191, 141, 0.1);
    }

    .navActionPlaceholder {
      background: transparent;
    }

    .navTitle {
      max-width: calc(100% - 64px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: PingFangSC, PingFang SC, sans-serif;
      font-size: 17px;
      line-height: 24px;
      font-weight: 500;
      color: #121212;
      text-align: left;
      font-style: normal;
    }
  }

  .modulePanel {
    margin: 0;
    padding: 20px 8px;
    border-radius: 0;
    background: #ffffff;
  }

  .moduleGrid {
    display: grid;
    grid-template-columns: repeat(4, 25%);
    row-gap: clamp(14px, 4.8vw, 20px);
  }

  .moduleItem {
    min-width: 0;
    width: 100%;
    min-height: clamp(76px, 22vw, 90px);
    padding: 0 1px;
    border: 0;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
  }

  .moduleIcon {
    width: clamp(44px, 13.6vw, 56px);
    height: clamp(44px, 13.6vw, 56px);
    border-radius: 50%;
    background: #dff5ff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center;
      display: block;
    }

    &.custom {
      background: linear-gradient(135deg, #dff5ff 0%, #c8eefb 100%);
    }
  }

  .pathIcon {
    position: relative;
    width: 32px;
    height: 28px;
    border-radius: 6px;
    background: #0c8ee8;

    &::before {
      content: '';
      position: absolute;
      left: 3px;
      top: -6px;
      width: 17px;
      height: 8px;
      border-radius: 5px 5px 0 0;
      background: #35b7f3;
    }

    i {
      position: absolute;
      left: 7px;
      right: 7px;
      top: 13px;
      height: 3px;
      border-radius: 2px;
      background: #ffffff;
      transform: rotate(-18deg);

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 0;
        width: 9px;
        height: 3px;
        border-radius: 2px;
        background: #ffffff;
      }

      &::before {
        left: -4px;
        transform: rotate(42deg);
      }

      &::after {
        right: -4px;
        transform: rotate(42deg);
      }
    }
  }

  .moduleName {
    width: 100%;
    max-width: 82px;
    margin-top: clamp(6px, 2vw, 9px);
    padding: 0 2px;
    font-size: clamp(12px, 3.5vw, 15px);
    line-height: 1.35;
    color: #333333;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: normal;
  }

  .searchBox {
    margin: 8px var(--lit-page-gutter) 0;
    height: 36px;
    padding: 0 5px 0 12px;
    display: flex;
    align-items: center;
    border: 1px solid #e7edf4;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(31, 41, 55, 0.04);

    > img {
      flex: 0 0 auto;
      width: 17px;
      height: 17px;
      opacity: 0.78;
      filter: invert(65%) sepia(40%) saturate(796%) hue-rotate(118deg) brightness(90%) contrast(88%);
    }

    input {
      min-width: 0;
      flex: 1;
      height: 34px;
      border: 0;
      background: transparent;
      padding: 0 7px;
      font-size: 13px;
      color: #333333;
      outline: none;

      &::placeholder {
        color: #a3adba;
      }
    }

    button {
      border: 0;
      background: transparent;
    }

    .cleanBtn {
      width: 26px;
      height: 26px;
      padding: 4px;

      img {
        width: 18px;
        height: 18px;
      }
    }

    .searchBtn {
      flex: 0 0 auto;
      min-width: 48px;
      height: 28px;
      padding: 0 10px;
      border-radius: 14px;
      border: 0;
      background: rgba(27, 191, 141, 0.12);
      color: var(--lit-soft-green);
      font-size: 13px;
      font-weight: 500;
    }
  }

  .pathTabs {
    height: 36px;
    display: flex;
    align-items: stretch;
    overflow-x: auto;
    background: #f8f8f8;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    button {
      position: relative;
      flex: 0 0 auto;
      min-width: 56px;
      max-width: 136px;
      height: 36px;
      padding: 0 10px;
      border: 0;
      background: transparent;
      color: #999999;
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.active {
        color: #1bbf8d;

        &::after {
          content: '';
          position: absolute;
          left: 50%;
          bottom: 0;
          width: 26px;
          height: 3px;
          border-radius: 2px;
          background: var(--lit-soft-green);
          transform: translateX(-50%);
        }
      }
    }
  }

  .filterTags {
    margin: 0 var(--lit-page-gutter) 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #98a2b3;

    button,
    span {
      border: 0;
      background: transparent;
      color: #667085;
      padding: 0;
      line-height: 20px;
    }

    span {
      padding: 0 9px;
      border-radius: 10px;
      background: rgba(27, 191, 141, 0.1);
      color: var(--lit-soft-green);
    }

    button {
      color: #ef4444;
    }
  }

  .content {
    margin: 10px var(--lit-page-gutter) 0;
    padding: 10px;
    min-height: 150px;
    border-radius: 8px;
    background: #ffffff;

    &.medicineRoot {
      margin: 14px 10px 0;
      padding: 0;
      min-height: 0;
      border-radius: 0;
      background: transparent;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px 0;

      ::v-deep .van-empty {
        grid-column: 1 / -1;
      }
    }

    ::v-deep .van-empty {
      width: 100%;
      min-height: 220px;
      padding: 34px 0;
      align-items: center;
      justify-content: center;
    }

    ::v-deep .van-empty__image {
      width: 90px;
      height: 90px;
      margin: 0 auto;
    }

    ::v-deep .van-empty__description {
      width: 100%;
      text-align: center;
      font-size: 13px;
      line-height: 18px;
    }
  }

  .loadingBox {
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .medicineCard {
    padding: 0 6px;

    img {
      width: 100%;
      height: clamp(66px, 20vw, 78px);
      border-radius: 6px;
      display: block;
      object-fit: cover;
    }
  }

  .listItem {
    min-height: 38px;
    padding: 8px 0 8px 2px;
    background: #ffffff;
    border-bottom: 1px solid #eeeeee;
    display: flex;
    align-items: center;
    gap: 8px;

    .itemMain {
      flex: 1;
      min-width: 0;
    }

    .itemTitle {
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: #333333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .itemDesc,
    .itemMeta {
      margin-top: 4px;
      font-size: 12px;
      line-height: 18px;
      color: #999999;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .itemBadge {
      flex: 0 0 auto;
      padding: 2px 7px;
      border-radius: 9px;
      background: rgba(27, 191, 141, 0.1);
      color: var(--lit-soft-green);
      font-size: 12px;
    }

    .arrowIcon {
      flex: 0 0 auto;
      width: 6px;
      height: 6px;
      margin-right: 4px;
      border-top: 1.5px solid #c5ccd6;
      border-right: 1.5px solid #c5ccd6;
      transform: rotate(45deg);
    }

    &.selected {
      .itemTitle,
      .itemDesc,
      .itemMeta {
        color: #1bbf8d;
      }
    }
  }

  .sectionTitle {
    margin: 10px 0 8px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: #202939;
  }

  .tagWrap {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }

  .tag {
    min-height: 30px;
    border: 0;
    border-radius: 4px;
    padding: 0 10px;
    background: transparent;
    color: #333333;
    font-size: 12px;
    line-height: 30px;

    &.selected {
      background: rgba(27, 191, 141, 0.1);
      color: var(--lit-soft-green);
    }
  }

  .filterPanel {
    height: 410px;
    max-height: 78vh;
    padding: 12px 0 20px;
    background: #ffffff;
    display: flex;
    flex-direction: column;

    .filterHeader {
      position: relative;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 12px;

      strong {
        font-size: 15px;
        color: #333333;
      }

      .closeBtn {
        position: absolute;
        right: 14px;
        top: -3px;
        width: 28px;
        height: 28px;
        border: 0;
        background: transparent;
        color: #999999;
        font-size: 24px;
        line-height: 28px;
      }
    }
  }

  .filterBody {
    flex: 1;
    min-height: 0;
    display: flex;
    border-top: 1px solid #f2f2f2;
  }

  .filterLeft {
    flex: 0 0 92px;
    background: #f8f8f8;
    overflow-y: auto;

    button {
      width: 100%;
      min-height: 40px;
      border: 0;
      background: transparent;
      color: #333333;
      font-size: 13px;

      &.active {
        background: #ffffff;
        color: var(--lit-soft-green);
        font-weight: 600;
      }
    }
  }

  .filterRight {
    flex: 1;
    min-width: 0;
    align-content: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 6px;
    padding: 10px 9px;
    overflow-y: auto;

    .tag {
      min-height: 30px;
      padding: 0 10px;
      background: #f8f8f8;
    }
  }

  .filterFooter {
    display: flex;
    justify-content: center;
    padding-top: 10px;

    button {
      width: 122px;
      height: 38px;
      border: 0;
      font-size: 14px;
    }

    .resetBtn {
      border-radius: 19px 0 0 19px;
      background: rgba(27, 191, 141, 0.1);
      color: var(--lit-soft-green);
    }

    .doneBtn {
      border-radius: 0 19px 19px 0;
      background: var(--lit-soft-green);
      color: #ffffff;
    }
  }

  .detailPage {
    min-height: calc(100vh - var(--lit-nav-height));
    margin: 0;
    padding: 0 0 14px;
    background: #ffffff;
    border-radius: 0;
    border: 0;

    .detailHeader {
      border-bottom: 2px solid #f2f2f2;
      padding: 9px var(--lit-page-gutter);
      margin-bottom: 0;

      h2 {
        margin: 0;
        font-size: 17px;
        line-height: 24px;
        font-weight: 400;
        color: #333333;
        letter-spacing: 0;
      }

      p {
        margin: 4px 0 0;
        font-size: 12px;
        line-height: 18px;
        color: #999999;
      }
    }

    .detailVideo {
      width: 100%;
      max-height: 210px;
      border-radius: 8px;
      background: #000000;
      margin: 10px var(--lit-page-gutter) 0;
      width: calc(100% - var(--lit-page-gutter) * 2);
    }

    .detailMeta {
      padding: 8px var(--lit-page-gutter);
      margin-bottom: 0;
      border-radius: 0;
      background: #ffffff;
      color: #999999;
      font-size: 12px;
      line-height: 18px;
    }

    .htmlContent {
      padding: 10px var(--lit-page-gutter) 0;
      font-size: 14px;
      line-height: 23px;
      color: #333333;
      word-break: break-word;

      ::v-deep p {
        margin: 0 0 8px;
      }

      ::v-deep img {
        max-width: 100%;
        height: auto;
        margin: 8px auto;
      }
    }

    .detailSections {
      padding: 10px var(--lit-page-gutter) 0;

      section {
        margin-bottom: 12px;
      }

      h3 {
        margin: 0 0 5px;
        font-size: 15px;
        line-height: 22px;
        color: #202939;
      }

      p {
        margin: 0;
        white-space: pre-line;
        font-size: 14px;
        line-height: 23px;
        color: #344054;
      }
    }

    .scoreTable {
      overflow-x: auto;
      margin: 12px var(--lit-page-gutter) 0;

      .scoreRow {
        min-width: 620px;
        display: grid;
        grid-template-columns: 48px 1fr 1fr 1.4fr 60px 60px;
        border-bottom: 1px solid #eef2f7;

        &.header {
          background: #f5f5f5;
          font-weight: 600;
        }

        span {
          padding: 8px 6px;
          font-size: 12px;
          line-height: 18px;
          color: #344054;
        }
      }
    }

    .fileLink {
      display: block;
      margin: 16px var(--lit-page-gutter) 0;
      min-height: 24px;
      line-height: 24px;
      text-align: left;
      border-radius: 0;
      background: transparent;
      color: #333333;
      font-size: 13px;
      text-decoration: none;

      &::before {
        content: '附件：';
        display: block;
        margin-bottom: 4px;
        font-size: 15px;
        font-weight: 600;
        color: #333333;
      }
    }
  }
}
</style>
